import { ActionTree } from 'vuex';
import { StudyPlanningState } from './state';
import RootState from '@/store/root_state';
import {
  getStudyPlanningQuery,
  getStudyPlanningBreakpointsQuery,
} from '@/store/queries/westerosPostgradStudyPlanningQueries';

import {
  setStudyPlanningMutation,
  updateStudyPlanningStateMutation,
  anticipateStudyPlanningWeekListsMutation,
} from '@/store/mutations/westerosPostgradStudyPlanningMutations';

import { MICROSERVICES_ENUM, graphqlQuery, graphqlMutation } from '@/helpers/apollo';
import { getChimeraToken } from '@/helpers/get-tokens';
import { PostgradTrial } from '@/models/postgradTrial/PostgradTrialModel';
import {
  GetStudyPlanningBreakpointsResponse,
  GetStudyPlanningBreakpointsVariables,
  SaveStudyPlanningResponse,
  SaveStudyPlanningVariables,
} from '@/helpers/postgradStudyPlanning/interfaces';

const updateStudyPlanningState = async (courseId: number, canGraduateEarly: boolean) => {
  const response = await graphqlMutation(
    MICROSERVICES_ENUM.STUDYPLANNING,
    updateStudyPlanningStateMutation,
    { courseId, canGraduateEarly },
  );

  return response?.data.studyPlanning;
};

const actions: ActionTree<StudyPlanningState, RootState> = {
  async GET_STUDY_PLANNING({ commit, getters }, courseId: number) {
    const earlyGraduationEligibility =
      getters.earlyGraduationEligibilityByCourseId(courseId) ?? false;
    let studyPlanning = await updateStudyPlanningState(courseId, earlyGraduationEligibility);

    if (!studyPlanning) {
      const response = await graphqlQuery(MICROSERVICES_ENUM.STUDYPLANNING, getStudyPlanningQuery, {
        courseId,
        canGraduateEarly: earlyGraduationEligibility,
      });

      studyPlanning = response?.data.getStudyPlanning;
      if (!studyPlanning) return;
    }

    commit('SET_STUDY_PLANNING', studyPlanning);
    commit('SET_WEEK', studyPlanning.currentWeek);
    commit('SET_WEEK_LISTS', studyPlanning.currentWeek?.listsAndOverdueLists);
    commit('SET_ANTICIPATED_WEEKS', studyPlanning.anticipatedWeeks);
    commit('SET_ANTICIPATED_WEEKS_LISTS', studyPlanning.anticipatedWeeks);
  },

  async GET_STUDY_PLANNING_BREAKPOINTS({ commit, getters }, courseId: number) {
    const descomplicaToken = getChimeraToken();
    const trial: PostgradTrial | null = getters.trialByDescomplicaCourseId(
      getters.getCurrentChimeraCourse._id,
    );
    const earlyGraduationEligibility = getters.earlyGraduationEligibilityByCourseId(courseId);

    const response = await graphqlQuery<
      GetStudyPlanningBreakpointsResponse,
      GetStudyPlanningBreakpointsVariables
    >(MICROSERVICES_ENUM.STUDYPLANNING, getStudyPlanningBreakpointsQuery, {
      courseId,
      descomplicaToken,
      trial: !!trial,
      canGraduateEarly: earlyGraduationEligibility,
    });

    const studyPlanningBreakpointsData = response?.data.getStudyPlanningBreakpoints;
    if (!studyPlanningBreakpointsData) return;

    commit('SET_CURRENT_CHIMERA_COURSE_CONTRACT_INFO', {
      contractDate: studyPlanningBreakpointsData.contractDate,
      contractExpirationDate: studyPlanningBreakpointsData.contractExpirationDate,
    });

    const { breakpoints, extensionBreakpoints } = studyPlanningBreakpointsData;

    commit('SET_STUDY_PLANNING_BREAKPOINTS', breakpoints);
    commit('SET_STUDY_PLANNING_EXTENSION_BREAKPOINTS', extensionBreakpoints);
  },

  async SAVE_STUDY_PLANNING(
    { commit, getters },
    {
      courseId,
      numberOfMonths,
    }: { courseId: number; descomplicaCourseId: string; numberOfMonths: number },
  ) {
    const descomplicaToken = getChimeraToken();
    const trial: PostgradTrial | null = getters.trialByDescomplicaCourseId(
      getters.getCurrentChimeraCourse._id,
    );
    const earlyGraduationEligibility = getters.earlyGraduationEligibilityByCourseId(courseId);

    const response = await graphqlMutation<SaveStudyPlanningResponse, SaveStudyPlanningVariables>(
      MICROSERVICES_ENUM.STUDYPLANNING,
      setStudyPlanningMutation,
      {
        courseId,
        descomplicaToken,
        numberOfMonths,
        trial: !!trial,
        canGraduateEarly: earlyGraduationEligibility,
      },
    );

    if (!response?.data?.saveStudyPlanning) return;

    commit('SET_STUDY_PLANNING', response.data.saveStudyPlanning);
    commit('SET_WEEK', response.data.saveStudyPlanning.currentWeek);
    commit('SET_WEEK_LISTS', response.data.saveStudyPlanning.currentWeek.listsAndOverdueLists);
  },

  async ANTICIPATE_STUDY_PLANNING_LISTS(
    { commit },
    { studyPlanningId, courseId }: { studyPlanningId: number; courseId: number },
  ): Promise<boolean> {
    const wasAbleToAnticipateReturn = await graphqlMutation(
      MICROSERVICES_ENUM.STUDYPLANNING,
      anticipateStudyPlanningWeekListsMutation,
      { studyPlanningId },
    );
    const wasAbleToAnticipate = wasAbleToAnticipateReturn?.data.anticipateWeek;

    if (!wasAbleToAnticipate) return false;

    const response = await graphqlQuery(MICROSERVICES_ENUM.STUDYPLANNING, getStudyPlanningQuery, {
      courseId,
    });

    const studyPlanning = response?.data.getStudyPlanning;
    if (!studyPlanning) return false;

    commit('SET_ANTICIPATED_WEEKS', studyPlanning.anticipatedWeeks);
    commit('SET_ANTICIPATED_WEEKS_LISTS', studyPlanning.anticipatedWeeks);

    return true;
  },
};

export default actions;
